import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { genTinyGUID } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LenderService } from '@shared/service/lender/lender.service';
import { BundleHelperService } from '@workflow/ecp-workflow/services/bundle-helper.service';
import { VisibleFunctionsService } from '@workflow/ecp-workflow/dynamic-app/service/logic-functions/visible-functions.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { OaoPageHelper } from '@workflow/ecp-workflow/oao/helper/oao-page.helper';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { coAppSwitch, getPageIterationId, processPath } from '@workflow/ecp-workflow/dynamic-app/service/utilities.service';
import { Disclosure, DisclosureApplicant } from '@workflow/ecp-workflow/dynamic-app/types/disclosure.type';
import { getFlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { coBorrowersPathForOLA } from '../../types/constants';
// eslint-disable-next-line max-len
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { PageService } from '@workflow/class/page.service';
import { OlaPageHelper } from '@workflow/ecp-workflow/ola/helper/ola-page.helper';
import { Observable, Subscriber } from 'rxjs';
import { CustomFunctionsService } from '@workflow/ecp-workflow/dynamic-app/service/logic-functions/custom-functions.service';
import { isPlatformBrowser } from '@angular/common';
import { ApplicationFillType } from '@workflow/ecp-shared/enum/enum';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaOnChangeFunctionsService {
  private logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  public inMemoryStorage = { isArrayRemoving: false, isArrayAdding: false };
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);
  private readonly customFunctionsService = inject(CustomFunctionsService);
  private readonly applicationApiService = inject(ApplicationApiService);
  constructor(
    private readonly lenderService: LenderService,
    private readonly bundleManagerService: BundleHelperService,
    private readonly visibleFunctionsService: VisibleFunctionsService,
    public readonly l360ApiService: L360Service,
    private readonly pageNavigationStateService: WorkflowPageNavigationStateService,
    private readonly flowHelper: OaoPageHelper,
    private readonly olaFlowHelper: OlaPageHelper,
    private readonly router: Router,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly userService: UserService,
    private readonly workflowService: WorkflowService,
    private readonly flowService: FlowService,
    private readonly pageService: PageService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {}

  /**
   * Removes the coborrower from the bundle
   * @param waitForTruncate If true, waits for the array removal to complete
   * @returns An observable to subscribe to when it is either complete, or queued.
   */
  private removeCoBorrowerImpl(waitForTruncate: boolean = false): Observable<void> {
    const path = 'mem:ola-add-or-remove-co-applicant-inprogress';
    const expectedStatePath = 'mem:ola-add-or-remove-co-applicant-expected';
    const actualRemoveFn = (observer: Subscriber<void>) => {
      const inProgress = this.statefulService.wrap(path);
      inProgress.value = true;
      const arraySize = this.statefulService.arraySize(coBorrowersPathForOLA);
      // prevent array shrinkage when already empty
      if (arraySize > 0) {
        const coBorrowerPages = this.workflowService.currentFlow.allPages.filter((t) => t.config.array === 'ola:coBorrowers');

        // Killing the page instance so it doesn't create child arrays instances like references and other
        // after deleting coapplicant
        coBorrowerPages.forEach((each) => {
          this.pageService.forceClear(each.instanceId);
        });
        // note - remove this and automation heloc test gets passed add/remove co-app, but on re-add
        // we no longer have values in the relationship to primary field
        // this is double weird since it is not used in our automation heloc test
        this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/creditType').value = null;
        setTimeout(() => {
          // automation hack - in manual scenarios this is not required, however when running automated e2e
          // the creditType set to null actually fires off a bunch of stuff that shouldn't be, leading to 500 errors
          // delay here ensures that the coBorrower removal happens after those things that shouldn't happen are done
          // this kills me inside and should be revaluated soon - chris
          this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value = false;
          this.statefulService.arrayTruncate(coBorrowersPathForOLA, () => {
            inProgress.value = false;
            if (waitForTruncate) {
              // if this is true we want to wait for the removal to be complete complete
              observer.next();
              observer.complete();
            }
          });
        }, 300);
      } else {
        // ensure we match expectations by ensuring false
        this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value = false;
        inProgress.value = false;
        if (waitForTruncate) {
          // if this is true we want to wait for the removal to be complete complete
          observer.next();
          observer.complete();
        }
      }
      if (!waitForTruncate) {
        observer.next();
        observer.complete();
      }
    };
    // use an in memory value to wait on in the wait for ready function
    this.statefulService.wrap(expectedStatePath).value = false;
    return new Observable<void>((observer) => {
      const isProcessing = this.statefulService.wrap(path);
      if (isProcessing.value !== true) {
        actualRemoveFn(observer);
      } else {
        // already in progress, so just log and queue
        this.logger.warn(
          `addCoBorrowerImpl: already in progress: ${isProcessing.value}: ${
            this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value
          }=${false}`
        , { component: 'OlaOnChangeFunctionsService' });
        const processingHandle = () => {
          isProcessing.unsubscribe(processingHandle);
          actualRemoveFn(observer);
        };
        isProcessing.onChange(processingHandle);
      }
    });
  }

  /**
   * Adds a co-borrower to the application. If this is a re-add, it will ensure the credit type
   * either way it will queue up the changes and ensure that hasCCoborrower is true
   * @param waitForAdd If true, waits for the array addition to complete
   * @returns An observable to subscribe to when it is either complete, or queued.
   */
  private addCoBorrowerImpl(waitForAdd: boolean = false): Observable<void> {
    const path = 'mem:ola-add-or-remove-co-applicant-inprogress';
    const expectedStatePath = 'mem:ola-add-or-remove-co-applicant-expected';
    const actualAddFn = (observer: Subscriber<void>) => {
      const isProcessing = this.statefulService.wrap(path);
      isProcessing.value = true;
      const coBorrowersCount = this.statefulService.arraySize(coBorrowersPathForOLA);
      // we only allow one co-borrower for loans - so block adding more
      if (coBorrowersCount < 1) {
        this.statefulService.arrayAdd(coBorrowersPathForOLA, () => {
          isProcessing.value = false;
          // in add-remove-add scenarios we need to ensure joint has value?
          // if they have co signer options, they will set that value then
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!(this.getLoanProductSimple() as any)?.allowCoSigner) {
            this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/creditType').value = 'JointCredit';
          }
          if (waitForAdd) {
            observer.next();
            observer.complete();
          }
        });
      } else {
        // no add required - just set in progress false and continue
        isProcessing.value = false;
        if (waitForAdd) {
          observer.next();
          observer.complete();
        }
      }
      this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value = true;
      if (!waitForAdd) {
        observer.next();
        observer.complete();
      }
    };
    // use an in memory value to wait on in the wait for ready function
    this.statefulService.wrap(expectedStatePath).value = true;
    return new Observable<void>((observer) => {
      const isProcessing = this.statefulService.wrap(path);
      if (isProcessing.value !== true) {
        actualAddFn(observer);
      } else {
        // already in progress, so just log and queue
        this.logger.warn(
          `addCoBorrowerImpl: already in progress: ${isProcessing.value}: ${
            this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value
          }=${true}`
        , { component: 'OlaOnChangeFunctionsService' });
        const processingHandle = () => {
          isProcessing.unsubscribe(processingHandle);
          actualAddFn(observer);
        };
        isProcessing.onChange(processingHandle);
      }
    });
  }

  /**
   * Based on primary applicant answer we are adding and removing coborrower
   * @param context logic fn field context
   */
  public toggleCoBorrower = (context: LogicFnContext): void => {
    const value = context.field.data.value as boolean;
    if (!value) {
      this.removeCoBorrowerImpl(false).subscribe();
    } else {
      this.addCoBorrowerImpl(false).subscribe();
    }
  };

  /**
   * Skip the instatouch page for a coapplicant and go to the next page
   * @param context logic fn field context
   */
  public skipCoBorrowerAndContinue = (context: LogicFnContext): void => {
    const previousPageId = context.page.previousPage.id;
    this.removeCoBorrowerImpl(true).subscribe(() => {
      const flow = this.flowService.createFlow(context.page.flowId);
      const nextPage = flow.pages.find((p) => p.id === previousPageId)?.nextPage;
      if (!nextPage) return;
      const flowPath = this.workflowService.getFlowPathWithPrefix(this.workflowService.currentFlow);
      this.router.navigateByUrl(`${flowPath}/${nextPage.path}`);
    });
  };

  /**
   * Removes the co-borrower and navigates back to the previous page.
   *
   * @param context - The logic function context containing workflow details.
   * @param currentPageId - The ID of the current page from which navigation is occurring.
   */
  public removeCoBorrowerAndGoBack = (context: LogicFnContext, currentPageId: string): void => {
    this.removeCoBorrowerImpl(true).subscribe(() => {
      const previousPage = this.workflowService.currentFlow.pages.find((x) => x.id === currentPageId);
      if (!previousPage) return;
      const flowPath = this.workflowService.getFlowPathWithPrefix(this.workflowService.currentFlow);
      this.router.navigateByUrl(`${flowPath}/${previousPage.path}`);
    });
  };

  public resetOLAMilitaryStatus = (context: LogicFnContext): void => {
    const militaryMapping = processPath(`ola:${coAppSwitch(context)}borrowerInfo/militaryServiceType`, context);
    if (context && context.field.data.value === false) {
      this.statefulService.wrap(militaryMapping).value = null;
    }
  };

  /**
   * Check whether more iterations should be added based on last cards' info
   * If there's no last card, we need at least one
   * @param context page and iteration info
   * @param requiredYears number of years required for adding new cards
   */
  public arrayAdjustOnMinimumYearForOLA = (context: LogicFnContext, requiredYears: number, fieldId?: string): void => {
    const field = fieldId
      ? this.fieldService.createField(fieldId, context.field.flowId, context.field.iterationId, context.field.pageIterationId)
      : context.field;
    const storageId = field.config.storageId;
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const pageArrayRoot = processPath(arrayRoot, context.field.pageIterationId);
    const maxLength = 7;

    if (
      !fieldId &&
      (this.statefulService.isArrayAdjusting(arrayRoot) ||
        (field.data.value && field.data.value.toString().length < maxLength) ||
        field.data.value === '')
    ) {
      return; // already adjusting
    }

    const currentSize = this.statefulService.arraySize(arrayRoot, context.field.pageIterationId);
    const iterationId = this.statefulService.arraySize(storageId, context.field.pageIterationId) - 1;
    const parts = this.statefulService.wrap(storageId, iterationId, context.field.pageIterationId).valueStringOnly.split('/');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const month = +parts.shift();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const year = +parts.shift();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const day = 1;

    const iterationIdPrev = iterationId - 1;
    const partsPrev = this.statefulService.wrap(storageId, iterationIdPrev, context.field.pageIterationId).valueStringOnly.split('/');
    const monthPrev = +partsPrev.shift();
    const yearPrev = +partsPrev.shift();
    const dayPrev = 1;

    if (currentSize > 0) {
      this.removeValuefromResidencyStorage(
        currentSize,
        context.field.config.storageId,
        context.field.pageIterationId,
        pageArrayRoot,
        requiredYears
      );
      const arrayValue = this.statefulService.wrap(storageId, currentSize - 1, context.field.pageIterationId);
      // const containerPrev = currentSize > 1;
      const isMissingTimeCoverage = this.visibleFunctionsService.missingTimeCoverage(context, storageId, +requiredYears);

      if (isMissingTimeCoverage && arrayValue != null) {
        this.statefulService.arrayAdd(pageArrayRoot, () => {
          setTimeout(() => {
            const addedIndex = this.statefulService.arraySize(`${arrayRoot}`, context.field.pageIterationId) - 1;
            // This was for #272878, somehow the 0 index residencyType was getting set to Prior
            // Not sure how this was occuring, but this is a workaround
            if (addedIndex > 0) {
              this.statefulService.wrap(`${arrayRoot}/$/residencyType`, addedIndex, context.field.pageIterationId).value = 'Prior';
            }
          }, 0);
        });
      } else if (this.olaisUpcoming(`${monthPrev}/${dayPrev}/${yearPrev}`, requiredYears)) {
        this.statefulService.arrayRemove(pageArrayRoot);
      }
    } else {
      this.statefulService.arrayAdd(pageArrayRoot, () => {
        setTimeout(() => {
          const addedIndex = this.statefulService.arraySize(`${arrayRoot}`, context.field.pageIterationId) - 1;
          // This was for #272878, somehow the 0 index residencyType was getting set to Prior
          // Not sure how this was occuring, but this is a workaround
          if (addedIndex > 0) {
            this.statefulService.wrap(`${arrayRoot}/$/residencyType`, addedIndex, context.field.pageIterationId).value = 'Prior';
          }
        }, 0);
      });
    }
  };

  private removeValuefromResidencyStorage(
    currentSize: number,
    storageId: string,
    pageIterationId: number,
    pageArrayRoot: string,
    requiredYears: number
  ) {
    for (let i = 0; i < currentSize; i++) {
      let currentValue = this.statefulService.wrap(storageId, i, pageIterationId).valueString;
      if (currentValue) {
        currentValue = currentValue.replace('/', '/01/');
      }
      if (this.olaisUpcoming(currentValue, requiredYears)) {
        for (let j = currentSize - 1; i < j; j--) {
          if (this.isBrowser) {
            // TODO: Evaluate the ms here, and need for setTimeout in general
            window.setTimeout(
              (temp_pageArrayRoot, temp_j) => {
                const currentAddressSize = this.statefulService.arraySize(temp_pageArrayRoot);
                if (temp_j < currentAddressSize) {
                  this.statefulService.arrayRemove(temp_pageArrayRoot, temp_j);
                }
              },
              1000,
              pageArrayRoot,
              j
            );
          }
        }
        break;
      }
    }
  }

  private olaisUpcoming = (date: string, requiredYears: number): boolean => {
    const startDate = new Date(date);
    return Date.now() >= startDate.setFullYear(startDate.getFullYear() + +requiredYears);
  };

  /**
   * validate and update array with given number of month
   * @param context page and iteration info
   * @param requiredMonth - minimum no of months
   */
  public arrayAdjustOnMinimumMonthOLA = (context: LogicFnContext, requiredMonth: number, isRetry = false): void => {
    const storageId = context.field.config.storageId;
    const pageIterationId = getPageIterationId(context);
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const currentSize = this.statefulService.arraySize(arrayRoot, pageIterationId);
    const isEmployementPage = arrayRoot.indexOf('employments') !== -1 ? true : false;
    let isHaveEmptyCard = false;
    let storagePrefix = arrayRoot;
    let totalMonths = 0;

    /** Minimum month update from Loan Product for Residence and Employment  */
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const selectedLoanProduct = this.GetSelectedProduct(loanType, loanGroup);
    if (isEmployementPage && selectedLoanProduct && selectedLoanProduct.minMonthEmployment) {
      requiredMonth = selectedLoanProduct.minMonthEmployment;
    } else if (selectedLoanProduct && selectedLoanProduct.minMonthResidence) {
      requiredMonth = selectedLoanProduct.minMonthResidence;
    }

    if (this.inMemoryStorage.isArrayAdding || this.inMemoryStorage.isArrayRemoving) {
      if (!isRetry) {
        // TODO: Evaluate the ms here, and need for setTimeout in general
        setTimeout(() => {
          this.arrayAdjustOnMinimumMonthOLA(context, requiredMonth, true);
        }, 600);
      }
      return;
    }

    if (pageIterationId >= 0) {
      storagePrefix = arrayRoot.replace('#', pageIterationId.toString());
    }

    if (!this.areMinTimeEnforced(arrayRoot)) {
      return; // not enforced
    }

    let deleteStartIndex;
    for (let i = 0; i < currentSize; i++) {
      if (totalMonths >= requiredMonth) {
        deleteStartIndex = i;
        break;
      }

      const yearsVal = this.statefulService.wrap(`${storagePrefix}/${i}/timeinProfessionYears`);
      const monthVal = this.statefulService.wrap(`${storagePrefix}/${i}/timeinProfessionMonths`);
      const year = yearsVal.value as number;
      const months = monthVal.value as number;
      totalMonths += year ? year * 12 : 0;
      totalMonths += months ? months : 0;

      // eslint-disable-next-line eqeqeq
      if ((year == undefined || year == null) && (months == undefined || months == null)) {
        isHaveEmptyCard = true;
      }
    }

    if (deleteStartIndex) {
      this.inMemoryStorage.isArrayRemoving = true;
      for (let i = currentSize - 1; i >= deleteStartIndex; i--) {
        this.statefulService.arrayRemove(storagePrefix, i, () => {
          this.inMemoryStorage.isArrayRemoving = false;
        });
      }
    }

    if (totalMonths < requiredMonth && !isHaveEmptyCard) {
      this.inMemoryStorage.isArrayAdding = true;
      this.statefulService.arrayAdd(storagePrefix, () => {
        setTimeout(() => {
          this.inMemoryStorage.isArrayAdding = false;
          const addedIndex = this.statefulService.arraySize(`${arrayRoot}`, context.field.pageIterationId) - 1;
          if (isEmployementPage) {
            this.statefulService.wrap(`${arrayRoot}/$/isPreviousEmployment`, addedIndex, context.field.pageIterationId).value = true;
          } else {
            // This was for #272878, somehow the 0 index residencyType was getting set to Prior
            // Not sure how this was occuring, but this is a workaround
            if (addedIndex > 0) {
              this.statefulService.wrap(`${arrayRoot}/$/residencyType`, addedIndex, context.field.pageIterationId).value = 'Prior';
            }
          }
        }, 0);
      });
    }
  };

  private areMinTimeEnforced(storagePrefix: string): boolean {
    const findNestedOrRootFieldMatching = (n) => (f) => {
      return (
        f.config.storageId === n ||
        f.nestedFields?.some((nf) => nf.config.storageId === n || nf.nestedFields?.some((dnf) => dnf.config.storageId === n))
      );
    };
    // get configuration for fields
    const yrPath = `${storagePrefix}/$/timeinProfessionYears`;
    let yrConfig = this.flowHelper.page?.cards
      .find((c) => c.helper.flatVisibleFields.some(findNestedOrRootFieldMatching(yrPath)))
      ?.helper?.flatVisibleFields?.find(findNestedOrRootFieldMatching(yrPath));
    if (yrConfig && yrConfig.config.storageId !== yrPath) {
      yrConfig = yrConfig.nestedFields.find(
        (f) => f.config.storageId === yrPath || f.nestedFields?.some((nf) => nf.config.storageId === yrPath)
      );
      if (yrConfig && yrConfig.config.storageId !== yrPath) {
        yrConfig = yrConfig.nestedFields.find((f) => f.config.storageId === yrPath);
      }
    }
    const mnPath = `${storagePrefix}/$/timeinProfessionMonths`;
    let mnConfig = this.flowHelper.page?.cards
      .find((c) => c.helper?.flatVisibleFields?.some(findNestedOrRootFieldMatching(mnPath)))
      ?.helper?.flatVisibleFields?.find(findNestedOrRootFieldMatching(mnPath));
    if (mnConfig && mnConfig.config.storageId !== mnPath) {
      mnConfig = mnConfig.nestedFields.find(
        (f) => f.config.storageId === mnPath || f.nestedFields?.some((nf) => nf.config.storageId === mnPath)
      );
      if (mnConfig && mnConfig.config.storageId !== mnPath) {
        mnConfig = mnConfig.nestedFields.find((f) => f.config.storageId === mnPath);
      }
    }

    const yearFieldMissingOrOptional = !yrConfig || !yrConfig.enabled || !yrConfig?.required || !yrConfig.visible;
    const monthFieldMissingOrOptional = !mnConfig || !mnConfig.enabled || !mnConfig?.required || !mnConfig.visible;
    if ((!yrConfig && !mnConfig) || (yearFieldMissingOrOptional && monthFieldMissingOrOptional)) {
      return false; // it is optional or hidden so skip?
    }
    return true; // at least one is required, enforce
  }

  public dynamicSecuredSubtitle = (context: LogicFnContext): string => {
    if (context && context.card) {
      const subtitle = context.card.config.subtitle;
      context.card.config.subtitle = subtitle.replace('#LenderName', this.lenderService.lenderData.lenderName);
    }
    return 'Account Securing Your Loan';
  };

  public dynamicCardTitleOLA = (context: LogicFnContext, storagePath: string): string => {
    if (context && context.card && context.card.iterationId) {
      const isPrevious = this.statefulService.wrap(storagePath, context.card.iterationId, context.card.pageIterationId).value;
      if (context.card.iterationId === 0) {
        return 'Current Employment';
      } else if (context.card.iterationId > 0 && isPrevious) {
        return 'Previous Employment';
      }
    }
    return 'Current Employment';
  };

  /**
   * Generates a dynamic card title based on the provided prefix, values, and context.
   *
   * @param context - The context of the logical function being executed.
   * @param prefix - A string or an array of title templates that may contain placeholders (`?`).
   * @param values - A list of string values to replace placeholders in the title.
   * @returns A dynamically generated title based on the context and provided values.
   *
   * @remarks
   * - If `prefix` is a string, it replaces `?` with the concatenated `values` or defaults to iteration ID.
   * - If `prefix` is an array, the appropriate indexed title is selected and formatted.
   * - If an array exists in `context.card.config`, it affects how the title is generated, particularly when its size is greater than 2.
   * - Ensures safe array indexing and handles cases where titles or values are missing.
   */
  public dynamicCardTitleForOLA = (context: LogicFnContext, prefix: string | Array<string>, ...values: string[]): string => {
    const capitalizedValues = values.map((v) => v.charAt(0).toUpperCase() + v.slice(1));
    const jointValues = capitalizedValues.join(' ');
    const pageIterationId = getPageIterationId(context);
    const arraySize = context.card.config.array ? this.statefulService.arraySize(context.card.config.array, pageIterationId) : 0;
    const iterationId = context.card.iterationId ?? 0;
    // Handle case when prefix is a string (not an array)
    if (!Array.isArray(prefix)) {
      const title2 = prefix.replace('?', jointValues);
      const defaultTitle2 = prefix.replace('?', (iterationId + 1).toString());
      return jointValues.trim() ? title2 : defaultTitle2;
    }
    const prefixIndex = Math.min(iterationId, prefix.length - 1);
    let title = prefix[prefixIndex].replace('?', jointValues);
    if (arraySize > 2) {
      title += iterationId >= 1 ? ` — ${iterationId}` : '';
    }
    const finalTitle = context.card.pageIterationId !== undefined ? `${context.card.pageIterationId + 1}` : '';
    let defaultTitle = context.card.config.title + finalTitle;
    if (Array.isArray(context.card.config.title)) {
      const titleIndex = Math.min(iterationId, context.card.config.title.length - 1);
      defaultTitle = context.card.config.title[titleIndex];
      if (arraySize > 2) {
        defaultTitle += iterationId >= 1 ? ` — ${iterationId}` : '';
      }
    }
    return jointValues.trim() ? title : defaultTitle;
  };

  public dynamicTitleOLA = (context: LogicFnContext, prefix: string, ...values: string[]): string => {
    const jointValues = values ? values.join(' ') : '';
    const hasCoBorrower: boolean = this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value as boolean;
    let pageTitle = '';
    if (!hasCoBorrower) {
      pageTitle = prefix ? prefix.replace('(?)', '') : jointValues;
    } else {
      pageTitle = prefix ? prefix.replace('?', jointValues) : jointValues;
    }
    return pageTitle;
  };

  /** since action page is invisible and displaying previous page title */
  public dynamicActionPageTitle = (context: LogicFnContext): string => {
    const title = context?.page?.previousPage?.customTitleOrDefault;
    return title ? title : context?.page?.title;
  };

  public onChangeLoanGroup = (value: string): void => {
    if (!this.statefulService.wrap('ss:ola-prev-loan-group').value) {
      this.statefulService.wrap('ss:ola-prev-loan-group').value = value;
      return;
    }

    if (this.statefulService.wrap('ss:ola-prev-loan-group').valueString === value) {
      return;
    }

    this.statefulService.wrap('ss:ola-new-loan-group').value = value;
    this.statefulService.wrap('ss:showLoanGroupChangeModal').value = true;
  };

  public onChangeAdditionalMonthlyRadio = (context: LogicFnContext): void => {
    if (context.field.data.value) {
      return;
    }

    const iterationID = getPageIterationId(context);
    let storagePath = 'ola:otherIncomes';
    if (iterationID >= 0) {
      storagePath = `ola:coBorrowers/${iterationID}/otherIncomes`;
    }
    const otherIncomesize = this.statefulService.arraySize(storagePath);
    const index = otherIncomesize - 1;
    for (let i = index; i > 0; i--) {
      this.statefulService.arrayRemove(storagePath, i);
    }
  };

  /**
   * Load OLA VehicleMake
   *   * @param _context - Field context
   *   * @param storagePath - Storage path make value
   *   * @param vehicleType - Vehicle type to filter
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public loadVehicleMake = (_context: LogicFnContext, storagePath: string, vehicleType: number): void => {
    const targetField = this.fieldService.createField(_context.field.id, _context.field.flowId);
    const isLoanGroupChanged = this.statefulService.wrap('ss:is-loangroup-got-changed-for-make').value;

    // Only call the API when the enum is not loaded. Once enum loaded here, then it will not make api call
    if (targetField?.enum?.length === 0 || isLoanGroupChanged) {
      this.statefulService.wrap('ss:is-loangroup-got-changed-for-make').value = false;
      targetField.data.value = null;
      const iterationID = getPageIterationId(_context);
      const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, iterationID).value;
      const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, iterationID).value;
      this.getLoanProduct(_context, storagePath, loanGroup, loanType, undefined);
    }
  };

  /**
   *  Get selected Loan Product
   *  @param _context - The context of the logical function being executed
   *  @param storagePath - Storage path make value
   *  @param loanGroup - Loan product
   *  @param loanType - Loan Type
   *  @param collateralTypeId - ID to filter collateralTypeId
   */
  private getLoanProduct = (
    _context: LogicFnContext,
    storagePath: string,
    loanGroup: string | number | boolean,
    loanType: string | number | boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collateralTypeId: any
  ): void => {
    if (this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
        this.getCollatralTypeId(_context, storagePath, selectedLoanProduct, collateralTypeId);
      }
    }
  };

  /**
   * Gets the loan product based on the current state of the application.
   * This method retrieves the loan product name and group name from the stateful service,
   * @returns The loan product if found, otherwise undefined.
   */
  private getLoanProductSimple(): unknown {
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value;
    if (this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
        return selectedLoanProduct;
      }
    }
  }

  /**
   *  Get collateralTypeId for OLA Vehicle Make
   *  @param _context - The context of the logical function being executed
   *  @param storagePath - Storage path make value
   *  @param selectedLoanProduct - Loan product to filter VehicleMake ID
   *  @param collateralTypeId - ID to filter collateralTypeId
   *  @return collateralTypeId
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getCollatralTypeId = (_context: LogicFnContext, storagePath: string, selectedLoanProduct: any, collateralTypeId: any): any => {
    if (selectedLoanProduct) {
      // Log picklist response to AI - REMOVE IN 218336
      const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
      const logSrc = 'workflow';
      this.logPicklistTiming('ecp.ui.picklist', 'start', eventPairId, logSrc, 'VehicleType');

      const picklistTypes = this.l360ApiService.getPickListTypes('VehicleType');
      if (picklistTypes?.length > 0) {
        const collateralType = picklistTypes;
        const selectedPickListItem = collateralType[0].pickListItems.find(
          (pickListItem) => pickListItem.value === selectedLoanProduct.collateralType
        );
        collateralTypeId = selectedPickListItem ? selectedPickListItem.id : undefined;
        this.getMakeValue(_context, storagePath, collateralTypeId, eventPairId, logSrc);
      }
    }
    return collateralTypeId;
  };

  /**
   * Get Make Value OLA Vehicle
   *  @param _context - The context of the logical function being executed
   *  @param storagePath - Storage path make value
   *  @param collateralTypeId -ID to get makeValue
   *  @param eventPairId - Log ID
   *  @param logSrc - Log Source
   */
  private getMakeValue = (
    _context: LogicFnContext,
    storagePath: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collateralTypeId: any,
    eventPairId: string,
    logSrc: string
  ): void => {
    const targetField = this.fieldService.createField(_context.field.id, _context.field.flowId);
    let makeValue = this.statefulService.wrap(storagePath).value;
    this.l360ApiService.getVehicleMake(collateralTypeId).subscribe(
      (response) => {
        if (response?.data?.length > 0) {
          let enumvalue;
          targetField.enum.length = 0;
          response.data.forEach((element) => {
            enumvalue = {
              enumId: element.id,
              label: element.value,
              value: element.value,
              virtual: false
            };
            targetField.enum.push(enumvalue);
          });
          this.bundleManagerService.refreshOLABundleAsync().then(() => {
            if (!makeValue) {
              makeValue = this.statefulService.wrap(storagePath).value;
            }
            targetField.data.value = makeValue;
          });
          this.logPicklistTiming('ecp.ui.picklist', 'end', eventPairId, logSrc, 'VehicleType');
        }
      },
      (err) => {
        this.logger.withError(err).error('Failed to submit marketing data to Silk', { component: 'OlaOnChangeFunctionsService' });
      }
    );
  };

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Event log for app Insights
   *   * @param _eventName - Log event name
   *   * @param _eventState - Log event status
   *   * @param _correlationId - ID of event
   *   * @param _source - Log Source
   *   * @param _keys - Log keys
   */
  public logPicklistTiming = (_eventName: string, _eventState: string, _correlationId: string, _source: string, _keys: string): void => {
    this.logEvent(_eventName, {
      eventState: _eventState,
      correlationId: _correlationId,
      source: _source,
      keys: _keys
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public toggleDisableNavbar = (_context: LogicFnContext): void => {
    this.statefulService.wrap('mem:reloadOLANavbar').load();
  };

  public resetMembershipID = (context: LogicFnContext): void => {
    if (context.field.data.value === false) {
      this.statefulService.wrap(`ola:olaApplicationInformation/membership/membershipID`).value = null;
    }
  };

  /**
   * Load OLA Disclosure Content
   * @param pageId
   */
  public loadOLADisclosure = (_context: LogicFnContext): Promise<boolean> => {
    const disclosure: Disclosure[] = [];
    let applicants: DisclosureApplicant[];
    const storagePath = 'ola:olaApplicationInformation/disclosures';
    const hasCoBorrower: boolean = this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value as boolean;
    /// Making a get call to update proper applicant disclosure
    return new Promise((resolve) => {
      this.statefulService.wrap('ola:borrowerInfo/applicationID').load(() => {
        const disclosuresCount = this.statefulService.arraySize(storagePath);
        for (let i = 0; i < disclosuresCount; i++) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let isPDFFile: any = this.statefulService.wrap(storagePath + `/${i}/isPDFFile`).value;
          isPDFFile = isPDFFile === 'false' ? false : true;
          const applicantsCount = this.statefulService.arraySize(storagePath + `/${i}/applicants`);
          const isLinkType = this.statefulService.wrap(`${storagePath}/${i}/isLinkType`).value as boolean;
          applicants = [];

          let isPDFDisclosure = false;
          for (let j = 0; j < applicantsCount; j++) {
            const isAccept = this.statefulService.wrap(storagePath + `/${i}/applicants/${j}/isAccept`).value as boolean;
            if (isAccept && isPDFFile) {
              isPDFDisclosure = true;
            }
            applicants.push({
              accepted: isAccept,
              storageId: storagePath + `/${i}/applicants/${j}/isAccept`,
              agreedDateStorageId: storagePath + `/${i}/applicants/${j}/acceptDateTime`,
              label: this.statefulService.wrap(storagePath + `/${i}/applicants/${j}/acceptText`).valueString,
              applicant: this.statefulService.wrap(storagePath + `/${i}/applicants/${j}/applicant`).valueString
            });
            // Remove non-primary applicants from applicants list if there is no co-borrower
            // Note: this will need to be enhanced if we have more than 1 co-applicants in the future
            if (!hasCoBorrower) {
              applicants.splice(1);
            }
          }

          disclosure.push({
            accepted: false,
            content: this.statefulService.wrap(storagePath + `/${i}/content`).valueString,
            allowToAccept: isPDFFile || isLinkType ? isPDFDisclosure : true,
            id: this.statefulService.wrap(storagePath + `/${i}/title`).valueString,
            isPDFFile,
            pdfFileName: this.statefulService.wrap(storagePath + `/${i}/pdfFileName`).valueString,
            title: this.statefulService.wrap(storagePath + `/${i}/title`).valueString,
            applicants,
            isOpen: false,
            isLinkType,
            url: this.statefulService.wrap(`${storagePath}/${i}/url`).valueString,
            urlDisplayText: this.statefulService.wrap(`${storagePath}/${i}/urlDisplayText`).valueString,
            acceptanceTextReview: this.statefulService.wrap(`${storagePath}/${i}/acceptanceTextReview`).valueString
          });
        }
        _context.field.config.content = JSON.stringify(disclosure);
        resolve(true);
      });
    });
  };

  public onChangeEmploymentType = (_context: LogicFnContext, isCoBorrower: boolean = false): void => {
    const pageIterationID = getPageIterationId(_context);
    const iterationId = _context.field.iterationId;
    const root = isCoBorrower ? `ola:coBorrowers/${pageIterationID}/` : 'ola:';
    this.statefulService.wrap(`${root}employments/${iterationId}/employerorBusinessName`).value = '';
    this.statefulService.wrap(`${root}employments/${iterationId}/positionOrTitle`).value = '';
    this.statefulService.wrap(`${root}employments/${iterationId}/timeinProfessionYears`).value = null;
    this.statefulService.wrap(`${root}employments/${iterationId}/timeinProfessionMonths`).value = null;
    this.statefulService.wrap(`${root}employments/${iterationId}/employerPhonenumber`).value = '';
    this.statefulService.wrap(`${root}employments/${iterationId}/extension`).value = '';
    this.statefulService.wrap(`${root}employments/${iterationId}/payFrequency`).value = '';
    this.statefulService.wrap(`${root}employments/${iterationId}/grossIncome`).value = null;

    // Delete prev employment array on employment type change
    const arrayRoot = `${root}employments`;
    const currentSize = this.statefulService.arraySize(arrayRoot);
    for (let i = currentSize - 1; i >= 1; i--) {
      this.statefulService.arrayRemove(arrayRoot, i);
    }
  };

  public onChangeAdditionalEmploymentType = (_context: LogicFnContext, isCoBorrower: boolean = false): void => {
    const pageIterationID = getPageIterationId(_context);
    const iterationId = _context.field.iterationId;
    const root = isCoBorrower ? `ola:coBorrowers/${pageIterationID}/` : 'ola:';
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/employerorBusinessName`).value = '';
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/positionOrTitle`).value = '';
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/timeinProfessionYears`).value = null;
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/timeinProfessionMonths`).value = null;
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/employerPhonenumber`).value = '';
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/payFrequency`).value = '';
    this.statefulService.wrap(`${root}additionalEmployments/${iterationId}/grossIncome`).value = null;
  };

  public loadPromoCode = (_context: LogicFnContext, storagePath: string, queryParamPath: string): void => {
    const initPromo = this.statefulService.wrap(storagePath).valueString;
    const isQueryString = this.statefulService.wrap(queryParamPath).value;


    if (!initPromo && !isQueryString) {
      this.statefulService.wrap(queryParamPath).value = true;
    }
    const promo = this.getFromSession('promocode');
    if (promo) {
      this.statefulService.wrap(storagePath).value = promo;
      this.statefulService.wrap(queryParamPath).value = false;
    }
    if (this.isBrowser) {
      sessionStorage.removeItem('promocode');
    }
  };

  /**
   * Set OAO PID enabled or not flag
   * @param _context Logic Func
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public setOAOLenderSettings = (_context: LogicFnContext): void => {
    /** Updating OAO related settings which comes from L360 */
    this.flowHelper.setOAOLenderSettings();
  };

  /**
   * Method to skip membership selection and move to eligibilty page based on queryString display name.
   */
  public skipMembershipByQueryParam = (_context: LogicFnContext, classicFieldId: string, enhancedFieldId: string): void => {
    const queryString = this.getFromSession('MembershipType');
    const page = this.workflowService.findFirstIncompletePage();
    const flow = this.flowService.createFlow(_context.page.flowId);
    // Added null checks here as it would error when a logged in user selected a membership type and then hit back in the browser.
    const currentPageId = flow.pages.findIndex((t) => t.id === _context.page.id);
    const incompletePageId = flow.pages.findIndex((t) => t.id === page?.id);
    /* Default to show a loader on the screen
    After Enabling loader it will be closed either in the else part with query string
    or in getMemberships after pulling membership list from api.
    or in the splash screen
    */
    this.workflowLoader.start('Loading please wait...', {
      animationId: 'padded',
      overlayMode: 'card',
      segments: 3
    });
    flow.showLoader();
    // This to preserve redirecting to actual flow when splash screen enabled on deep link.
    if (!!page?.id && incompletePageId < currentPageId) {
      if (!queryString) {
        // Close any loader on the screen if query string is empty
        flow.closeLoader();
        this.workflowLoader.hide();
      }
      return;
    }

    if (queryString) {
      // Based on lendr config setting, set the field Id
      const fieldId = this.lenderService.lenderData.enableEnhancedProductSelection ? enhancedFieldId : classicFieldId;
      const field = this.fieldService.createField(fieldId, _context.page?.flowId);
      if (!field.config.payload) {
        field.config.payload = {};
      }
      this.setPrefillByParam(true);
      this.getMemberships(_context, field, flow, queryString);
    } else {
      // Close any loader on the screen if query string is empty
      flow.closeLoader();
      this.workflowLoader.hide();
    }
    sessionStorage.removeItem('MembershipType');
  };

  /**
   * get membership
   * @param _context
   * @param field
   * @param queryString
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getMemberships(_context: LogicFnContext, field: FieldInstance, currentFlow: FlowInstance, queryString: string): void {
    field.config.payload.isLoading = true;

    // Set Skip Eligibility Page Flow
    this.setSkipEligibilityFlow(queryString);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.l360ApiService.getMemberships().subscribe((response: any) => {
      const memberships = response?.data;
      if (!memberships) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let membershipsList: any[] = [];
      membershipsList = this.filterMembershipResponse(response);

      // eslint-disable-next-line @typescript-eslint/dot-notation
      field.config.payload['dropdownLists'] = JSON.stringify(membershipsList.filter((i) => i.isHideMembershipSelection !== true));
      field.config.payload.isLoading = false;

      const selectedMembership =
        membershipsList?.filter((i) => i.label.toLocaleLowerCase().trim() === queryString.toLocaleLowerCase().trim()) || [];
      if (selectedMembership.length < 1 || !currentFlow.config.streamlined) {
        // Close any loader on the screen
        currentFlow.closeLoader();
        this.workflowLoader.hide();
      }

      if (selectedMembership.length > 0) {
        this.userService.flowType = 'OAO';
        this.setSelectedMembership(selectedMembership);
        var autofillOption = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').valueString;

        field.data.value = selectedMembership[0].value;
        this.flowHelper.setStateValue(selectedMembership);
        /** Redirect to Next InComplete Page */
        const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
        const redirectURL = this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').valueString;
        const flow = this.flowService.createFlow(redirectURL || `oao/${membershipTypeName.toLowerCase()}`);
        if (flow.config.streamlined) {
          this.workflowLoader.start('Loading please wait...', {
            animationId: 'documentscan',
            overlayMode: 'card',
            segments: 3
          });
        }
        // If prefill option is DXPrefill (Dynamic App) then call prefillIdentificationType So the dynamic idtype populate much earlier
        if (autofillOption === ApplicationFillType.DXPrefill) {
          this.customFunctionsService.prefillIdentificationType(_context, 'oao-id-type');
          // Save bundle to make sure selected membership updated to Bundle before moving to next page.
          this.applicationApiService.saveBundleToDDMS(() => this.checkAndNavigateForDeeplinkedMembership(_context));
        } else {
          // Simply navigate with other deeplinked membership bundle will be saved on the going to next page
          this.flowHelper.updateOAOHBPreFillDataToBundle().then(() => {
            this.checkAndNavigateForDeeplinkedMembership(_context);
          });
        }
      } else {
        this.setPrefillByParam(false);
      }
    });
  }

  /// Navigate to next incomplete page after setting deeplinked membership
  private checkAndNavigateForDeeplinkedMembership(_context) {
    /** Redirect to Next InComplete Page */
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const redirectURL = this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').valueString;
    const flow = this.flowService.createFlow(redirectURL || `oao/${membershipTypeName.toLowerCase()}`);
    let firstVisiblePage = '';
    const visblePages = flow.pages.filter((t) => t.visible && t.id !== 'oao-membership');
    if (visblePages && visblePages.length > 0) {
      this.pageNavigationStateService.update(_context.page.id, { state: 'completed' });
      const flowInfo = getFlowInfo(flow);
      firstVisiblePage = `${flowInfo.configPath}/${visblePages[0].path}`;
      this.router.navigateByUrl(firstVisiblePage);
      return;
    }
  }

  /**
   * Get Redirect Url Path from Membership Selection
   * @param queryString string
   * @returns string
   */
  private setSkipEligibilityFlow(queryString: string): void {
    // TODO : Revist mebership filter and type
    const membershipList = this.adminPortalFeaturesService?.config?.oaoConfiguration?.memberships?.filter(
      (item) => item?.membershipName?.trim().toLowerCase() === queryString?.trim().toLowerCase()
    );

    if (membershipList?.length) {
      const flow = this.flowService.createFlow(membershipList[0].redirectUrl);
      const eligiblity = flow.allPages.find((t) => t.id === 'oao-eligibility');
      this.statefulService.wrap('api:skipEligibility').value = !(eligiblity?.visible || false);
    }
  }

  /**
   * Set mem variable by checking Is Prefilled By Param
   * Also hide page progress bar if Prefilled by Param
   * @param isPrefilled Boolean
   */
  private setPrefillByParam(isPrefilled: boolean): void {
    this.statefulService.wrap('mem:product-selection-prefilled-by-param').value = isPrefilled;
  }

  /**
   * filter memberships from api response
   * @param response
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private filterMembershipResponse(response: any): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const membershipsList: any[] = [];
    const dropdownValues = response.data.memberships.filter((membership) =>
      ['personal', 'business'].includes(membership.membershipTypeName.toString().toLowerCase())
    );

    const organizationID = response.data.organizationID ? response.data.organizationID : 0;
    this.statefulService.wrap('api:oaoMemberInformation/membership/organizationID').value = Number(organizationID);

    dropdownValues.forEach((element) => {
      const enumvalue = {
        enumId: element.membershipTypeName,
        membershipTypeID: element.membershipTypeID,
        isYouth: element.isYouth,
        label: element.membershipName,
        maxAge: element.maxAge,
        description: element.description,
        isEnabledChecklist: element.isEnabledChecklist,
        checklistDescription: element.checklistDescription,
        membershipFee: element.membershipFee,
        minAge: element.minAge,
        value: element.membershipID?.toString(),
        virtual: false,
        isPromoCode: element.maxPromoCodeDisplay > 0,
        organizationID,
        redirectURL: element.redirectURL,
        isHideMembershipSelection: element.isHideMembershipSelection
      };
      membershipsList.push(enumvalue);
    });

    membershipsList.sort((a, b) => a.label.localeCompare(b.label));

    return membershipsList;
  }

  /**
   * set membership
   * @param selectedMembership
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setSelectedMembership(selectedMembership: any[]): void {
    this.statefulService.wrap('ss:oao-first-login').value = true;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value = selectedMembership[0].value
      ? selectedMembership[0].value
      : '';
    this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipType').value = selectedMembership[0].enumId
      ? selectedMembership[0].enumId
      : null;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipTypeID').value = selectedMembership[0].membershipTypeID
      ? selectedMembership[0].membershipTypeID
      : null;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/isYouth').value = selectedMembership[0].isYouth;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/description').value = selectedMembership[0].description;
    this.statefulService.wrap('ss:oaoMemberInformation/membership/isEnabledChecklist').value = selectedMembership[0].isEnabledChecklist;
    this.statefulService.wrap('ss:oaoMemberInformation/membership/checklistdescription').value = selectedMembership[0].checklistDescription;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/redirectURL').value = selectedMembership[0].redirectURL;
    this.statefulService.wrap('mem:oaoMemberInformation/membership/isPromoCode').value = selectedMembership[0].isPromoCode;

    // Set the maxPromoCodeDisplayed flag to true to show the promo code on the review page/add questions page after refresh or re-login
    // Enhances for US #321017
    // As soon as we merge membership call with appInit, we will have maxPromoCodeDisplay value from the membership meta data
    // We wont need to save to the bundle any longer as its already there from appInit call
    // Set the promo code state
    // PROMO-TBD: We can remove this once maxPromoCodeDisplay is availiable in appInit for memberships
      const promoCodeCount = (selectedMembership[0]?.maxPromoCodeDisplay > 0 || selectedMembership[0].isPromoCode) ? 1 : 0;
      this.statefulService.wrap('api:oaoMemberInformation/membership/maxPromoCodeDisplay').value = promoCodeCount;

  }

  private getFromSession(key: string): string | undefined {
    if (!this.isBrowser) {
      return undefined;
    }
    return !sessionStorage.getItem(key) ? undefined : sessionStorage.getItem(key);
  }

  public showOLAPromotionCode = (): boolean => {
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    const selectedLoanProduct = this.GetSelectedProduct(loanType, loanGroup);
    const isProductVisible = (selectedLoanProduct && selectedLoanProduct.allowPromotionCode) as boolean;
    const olaAllowPromoCode = this.adminPortalFeaturesService?.config?.olaFeatures?.allowPromotionCode;
    return olaAllowPromoCode && isProductVisible;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public GetSelectedProduct = (loanType: string, loanGroup: string): any => {
    if (this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
        return selectedLoanProduct;
      }
    }
  };

  /**
   * Toggle OLA Co-Borrowers Mailing Address
   *   @param context - Field context
   *   @param rootPath - Storage root path value
   */
  public toggleCoBorrowersMailingAddress = (context: LogicFnContext, rootPath: string): void => {
    const pageIterationId = getPageIterationId(context);
    const storageId = context.field.config.storageId;
    const initStorage = storageId.includes('ola:coBorrowers') ? 'ola:coBorrowers/' : null;

    if (initStorage != null) {
      if (!context.field.data.value) {
        this.statefulService.wrap(`${initStorage}${pageIterationId}${rootPath}/address1`).value = null;
        this.statefulService.wrap(`${initStorage}${pageIterationId}${rootPath}/address2`).value = null;
        this.statefulService.wrap(`${initStorage}${pageIterationId}${rootPath}/city`).value = null;
        this.statefulService.wrap(`${initStorage}${pageIterationId}${rootPath}/state`).value = null;
        this.statefulService.wrap(`${initStorage}${pageIterationId}${rootPath}/zip`).value = null;
      }
    }
  };

  /**
   * Set flag to hide Yes/No quetion in Eligibility page based on its application template's "hideEligibilityQuestions"
   * @param context
   */
  public setEligibilityQuestionFlag = (context: LogicFnContext) => {
    const hideEligibilityQuestion = context.page?.config?.hideEligibilityQuestions || false;

    this.statefulService.wrap('ss:showEligibilityQuestions').value = !hideEligibilityQuestion;

    if (context.page?.id === 'ola-eligibility') {
      const alreadyMember = this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value;
      this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value = hideEligibilityQuestion
        ? false
        : alreadyMember;
    }

    if (context.page?.id === 'oao-eligibility') {
      const alreadyMember = this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value;
      this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value = hideEligibilityQuestion ? false : alreadyMember;
    }
  };

  /**
   * Set Default Address for Primary & Co-Applicant
   * @param context  - Field context
   * @param rootPath - Storage root path value
   * @param isCoBorrower - Flag to determine if it's a CoBorrower
   */
  public setDefaultMailingAddressOnLoad = (context: LogicFnContext, rootPath: string, isCoBorrower: boolean): void => {
    if (isCoBorrower) {
      const pageIterationId = getPageIterationId(context);
      const storagePath = rootPath.replace('#', pageIterationId.toString());

      if (!this.statefulService.wrap(`ola:coBorrowers/${pageIterationId}/borrowerInfo/mailingAddress/address1`).value) {
        this.statefulService.wrap(storagePath).value = true;
        return;
      }

      this.statefulService.wrap(storagePath).value = false;
    } else {
      if (!this.statefulService.wrap('ola:borrowerInfo/mailingAddress/address1').value) {
        this.statefulService.wrap(rootPath).value = true;
        return;
      }

      this.statefulService.wrap(rootPath).value = false;
    }
  };

  // Trigger OLA Compare and Fraud Verification
  public runOLACompareAndFraudVerification = async (context: LogicFnContext, isCoBorrower: boolean, edit: string): Promise<void> => {
    await this.olaFlowHelper.runOLACompareAndFraudVerificationAsync(isCoBorrower);
    this.statefulService.wrap(edit, undefined, context.pageIteration).value = false; // Reset the edit state
    context.defaultNavigationFn();
  };
}
